// src/pages/analytics/FinancialAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, Chip, useTheme, alpha,
  Breadcrumbs, Link, Tab, Tabs, CircularProgress, Button,
  MenuItem, Select, FormControl, InputLabel, Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as AccountIcon,
  Receipt as InvoiceIcon,
  Assessment as ROIIcon,
  ArrowUpward as ProfitIcon,
  NavigateNext as NavigateNextIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, AreaChart, Area,
  ComposedChart
} from 'recharts';

// Import custom components
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import MetricComparison from '../../components/features/analytics/MetricComparison';
import ExportButton from '../../components/features/analytics/ExportButton';
import AnalyticsFilters from '../../components/features/analytics/AnalyticsFilters';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import services
import { exportAnalyticsData } from '../../services/analyticsService';

// Import utils
import { formatNumber } from '../../utils/analyticsUtils';

// Import responsive styles
import { 
  getKpiGridProps, 
  getChartGridProps, 
  getPageHeaderStyles,
  getTabsContainerStyles,
} from '../../styles/analyticsResponsive';

/**
 * Financial Analytics Page Component
 */
const FinancialAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  // Define filter state
  const [filters, setFilters] = useState({
    clientId: 'all',
    brandId: 'all',
    campaignId: 'all',
    invoiceStatus: 'all',
    serviceType: 'all',
  });
  
  const { 
    financialAnalytics, isLoading, criticalError, fetchFinancialAnalytics,
    dateRange, setDateRange, clearError,
  } = useAnalyticsStore();
  
  const { showError } = useNotificationStore();
  
  // Define filter options
  const filterOptions = [
    {
      key: 'clientId',
      label: 'Client',
      type: 'select',
      options: [
        { value: 'all', label: 'All Clients' },
        { value: 'client1', label: 'Acme Corp' },
        { value: 'client2', label: 'Global Media' },
        { value: 'client3', label: 'Tech Solutions' },
        { value: 'client4', label: 'Fashion Brand' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'campaignId',
      label: 'Campaign',
      type: 'select',
      options: [
        { value: 'all', label: 'All Campaigns' },
        { value: 'campaign1', label: 'Summer Collection' },
        { value: 'campaign2', label: 'Product Launch' },
        { value: 'campaign3', label: 'Holiday Special' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'invoiceStatus',
      label: 'Invoice Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'paid', label: 'Paid' },
        { value: 'pending', label: 'Pending' },
        { value: 'overdue', label: 'Overdue' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'serviceType',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'all', label: 'All Services' },
        { value: 'social', label: 'Social Media Management' },
        { value: 'content', label: 'Content Creation' },
        { value: 'design', label: 'Design Services' },
        { value: 'consulting', label: 'Consulting' },
      ],
      defaultValue: 'all',
    },
  ];
  
  // Fetch data when filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchFinancialAnalytics({
          clientId: filters.clientId !== 'all' ? filters.clientId : undefined,
          brandId: filters.brandId !== 'all' ? filters.brandId : undefined,
          campaignId: filters.campaignId !== 'all' ? filters.campaignId : undefined,
          invoiceStatus: filters.invoiceStatus !== 'all' ? filters.invoiceStatus : undefined,
          serviceType: filters.serviceType !== 'all' ? filters.serviceType : undefined,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0],
        });
      } catch (error) {
        showError(`Failed to load financial analytics: ${error.message || 'Unknown error'}`);
      }
    };
    
    loadData();
  }, [fetchFinancialAnalytics, showError, dateRange, filters]);
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  
  // Handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Prepare export params
      const exportParams = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        // Include filter parameters
        clientId: filters.clientId !== 'all' ? filters.clientId : undefined,
        campaignId: filters.campaignId !== 'all' ? filters.campaignId : undefined,
        invoiceStatus: filters.invoiceStatus !== 'all' ? filters.invoiceStatus : undefined,
        serviceType: filters.serviceType !== 'all' ? filters.serviceType : undefined,
        reportType: 'financial',
      };
      
      // Call the export service
      await exportAnalyticsData(exportParams);
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // Check if data is available
  const hasData = !isLoading && financialAnalytics;
  
  // Loading state
  if (isLoading && !financialAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  // Error state
  if (criticalError && !financialAnalytics) {
    return (
      <Box sx={{ py: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link color="inherit" href="/analytics">Analytics</Link>
          <Typography color="text.primary">Financial Analytics</Typography>
        </Breadcrumbs>
        
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => clearError()}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => {
                  clearError();
                  fetchFinancialAnalytics();
                }}
              >
                Retry
              </Button>
            }
          >
            {criticalError}
          </Alert>
        </Box>
      </Box>
    );
  }
  
  // Mock data for development - replace with real data from API
  const revenueData = [
    { month: 'Jan', revenue: 45000, expenses: 32000, profit: 13000 },
    { month: 'Feb', revenue: 52000, expenses: 35000, profit: 17000 },
    { month: 'Mar', revenue: 49000, expenses: 33000, profit: 16000 },
    { month: 'Apr', revenue: 58000, expenses: 36000, profit: 22000 },
    { month: 'May', revenue: 62000, expenses: 38000, profit: 24000 },
    { month: 'Jun', revenue: 64000, expenses: 39000, profit: 25000 },
  ];
  
  const serviceRevenueData = [
    { name: 'Social Media', value: 35 },
    { name: 'Content Creation', value: 25 },
    { name: 'Design', value: 20 },
    { name: 'Consulting', value: 15 },
    { name: 'Other', value: 5 },
  ];
  
  const invoiceStatusData = [
    { status: 'Paid', value: 78 },
    { status: 'Pending', value: 18 },
    { status: 'Overdue', value: 4 },
  ];
  
  const clientRevenueData = [
    { name: 'Acme Corp', value: 145000 },
    { name: 'Global Media', value: 120000 },
    { name: 'Tech Solutions', value: 95000 },
    { name: 'Fashion Brand', value: 85000 },
    { name: 'Others', value: 55000 },
  ];
  
  const campaignROIData = [
    { name: 'Summer Collection', roi: 325, budget: 45000, revenue: 146250 },
    { name: 'Product Launch', roi: 275, budget: 65000, revenue: 178750 },
    { name: 'Holiday Special', roi: 410, budget: 35000, revenue: 143500 },
    { name: 'Brand Awareness', roi: 180, budget: 25000, revenue: 45000 },
    { name: 'Loyalty Program', roi: 250, budget: 30000, revenue: 75000 },
  ];
  
  const quarterlyPerformance = [
    { quarter: 'Q1', revenue: 145000, target: 140000 },
    { quarter: 'Q2', revenue: 165000, target: 160000 },
    { quarter: 'Q3', revenue: 155000, target: 170000 },
    { quarter: 'Q4', revenue: 220000, target: 210000 },
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')} 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Financial Analytics</Typography>
        </Breadcrumbs>
        
        <Box sx={getPageHeaderStyles(theme)}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Financial Analytics
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <AnalyticsFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              filterOptions={filterOptions}
              showDateRange={true}
            />
            
            <ExportButton
              onExport={handleExportAnalytics}
              formats={['excel', 'pdf', 'csv']}
              buttonText="Export Report"
              size="small"
            />
          </Box>
        </Box>
      </Box>
      
      {/* Error message */}
      {criticalError && (
        <Alert 
          severity="error" 
          onClose={() => clearError()}
          sx={{ mb: 4 }}
        >
          {criticalError}
        </Alert>
      )}
      
      {/* Overview KPIs */}
      <Grid container {...getKpiGridProps(theme).container}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Revenue"
            value={500000}
            previousValue={450000}
            percentChange={11.1}
            unit="$"
            icon={<MoneyIcon />}
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Profit"
            value={175000}
            previousValue={150000}
            percentChange={16.7}
            unit="$"
            icon={<ProfitIcon />}
            color="success"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Average ROI"
            value={285}
            unit="%"
            previousValue={270}
            percentChange={5.6}
            icon={<ROIIcon />}
            color="secondary"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Outstanding Invoices"
            value={45000}
            unit="$"
            previousValue={52000}
            percentChange={-13.5}
            icon={<InvoiceIcon />}
            color="warning"
            isLoading={isLoading}
            magical
            invertUpDown
          />
        </Grid>
      </Grid>
      
      {/* Content tabs */}
      <Box sx={getTabsContainerStyles(theme)}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<MoneyIcon />} iconPosition="start" label="Revenue" />
          <Tab icon={<InvoiceIcon />} iconPosition="start" label="Invoices" />
          <Tab icon={<ROIIcon />} iconPosition="start" label="ROI Analysis" />
          <Tab icon={<CampaignIcon />} iconPosition="start" label="Campaign Finance" />
        </Tabs>
      </Box>
      
      {/* Revenue Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Revenue & Profit Trends"
              subtitle="Financial performance over time"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={revenueData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left" 
                    dataKey="revenue" 
                    name="Revenue" 
                    fill={theme.palette.primary.main} 
                    barSize={20} 
                  />
                  <Bar 
                    yAxisId="left" 
                    dataKey="expenses" 
                    name="Expenses" 
                    fill={theme.palette.error.main} 
                    barSize={20} 
                  />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="profit" 
                    name="Profit" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={3} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Revenue by Service"
              subtitle="Percentage breakdown"
              icon={<MoneyIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={serviceRevenueData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {serviceRevenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={7}>
            <ChartCard
              title="Revenue by Client"
              subtitle="Top revenue generating clients"
              icon={<MoneyIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={clientRevenueData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis type="number" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    tick={{ fill: theme.palette.text.secondary }}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Bar dataKey="value" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={5}>
            <ChartCard
              title="Quarterly Performance"
              subtitle="Revenue vs Target"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              color="info"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={quarterlyPerformance}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="quarter" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="revenue" 
                    name="Revenue" 
                    fill={theme.palette.primary.main} 
                    barSize={30} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    name="Target" 
                    stroke={theme.palette.warning.main} 
                    strokeWidth={3} 
                    dot={{ stroke: theme.palette.warning.main, strokeWidth: 2, r: 6 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Invoices Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={5}>
            <ChartCard
              title="Invoice Status"
              subtitle="Current invoice status breakdown"
              icon={<InvoiceIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={invoiceStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="status"
                    label={(entry) => `${entry.status}: ${entry.value}%`}
                    labelLine={false}
                  >
                    <Cell fill={theme.palette.success.main} /> {/* Paid */}
                    <Cell fill={theme.palette.warning.main} /> {/* Pending */}
                    <Cell fill={theme.palette.error.main} /> {/* Overdue */}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name, props) => [`${value}%`, props.payload.status]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={7}>
            <ChartCard
              title="Payment Timeline"
              subtitle="Invoice payments over time"
              icon={<InvoiceIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                  data={[
                    { month: 'Jan', paid: 42000, pending: 8000, overdue: 2000 },
                    { month: 'Feb', paid: 48000, pending: 6000, overdue: 3000 },
                    { month: 'Mar', paid: 45000, pending: 9000, overdue: 1000 },
                    { month: 'Apr', paid: 55000, pending: 7000, overdue: 2000 },
                    { month: 'May', paid: 58000, pending: 9000, overdue: 1500 },
                    { month: 'Jun', paid: 60000, pending: 10000, overdue: 2000 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="overdue" 
                    name="Overdue" 
                    stackId="1" 
                    fill={alpha(theme.palette.error.main, 0.6)} 
                    stroke={theme.palette.error.main} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="pending" 
                    name="Pending" 
                    stackId="1" 
                    fill={alpha(theme.palette.warning.main, 0.6)} 
                    stroke={theme.palette.warning.main} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="paid" 
                    name="Paid" 
                    stackId="1" 
                    fill={alpha(theme.palette.success.main, 0.6)} 
                    stroke={theme.palette.success.main} 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Payment History"
              subtitle="Average days to payment"
              icon={<InvoiceIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { client: 'Acme Corp', avgDays: 15, onTime: 95 },
                    { client: 'Global Media', avgDays: 22, onTime: 85 },
                    { client: 'Tech Solutions', avgDays: 18, onTime: 90 },
                    { client: 'Fashion Brand', avgDays: 30, onTime: 75 },
                    { client: 'Others', avgDays: 25, onTime: 80 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="client" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left" 
                    dataKey="avgDays" 
                    name="Avg. Days to Payment" 
                    fill={theme.palette.primary.main} 
                    barSize={30} 
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="onTime" 
                    name="On-Time Payment %" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={3} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* ROI Analysis Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Campaign ROI Comparison"
              subtitle="Return on investment by campaign"
              icon={<ROIIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={campaignROIData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: theme.palette.text.secondary }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value, name) => {
                      if (name === 'roi') return [`${value}%`, 'ROI'];
                      return [`$${value.toLocaleString()}`, name];
                    }}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="roi" name="ROI %" fill={theme.palette.success.main} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Budget vs. Revenue"
              subtitle="Campaign financial performance"
              icon={<ROIIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={campaignROIData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: theme.palette.text.secondary }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="budget" name="Budget" fill={theme.palette.primary.main} barSize={20} />
                  <Bar dataKey="revenue" name="Revenue" fill={theme.palette.success.main} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Service ROI Analysis"
              subtitle="ROI comparison by service type"
              icon={<ROIIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={[
                    { service: 'Social Media', cost: 65000, revenue: 220000, roi: 338 },
                    { service: 'Content Creation', cost: 48000, revenue: 145000, roi: 302 },
                    { service: 'Design', cost: 35000, revenue: 95000, roi: 271 },
                    { service: 'Consulting', cost: 25000, revenue: 85000, roi: 340 },
                    { service: 'Other', cost: 12000, revenue: 30000, roi: 250 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="service" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value, name) => {
                      if (name === 'roi') return [`${value}%`, 'ROI'];
                      return [`$${value.toLocaleString()}`, name];
                    }}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="cost" name="Cost" fill={theme.palette.error.main} barSize={15} />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill={theme.palette.success.main} barSize={15} />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="roi" 
                    name="ROI %" 
                    stroke={theme.palette.secondary.main} 
                    strokeWidth={3} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Campaign Finance Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Campaign Budget Utilization"
              subtitle="Budget spent vs. allocated"
              icon={<CampaignIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { campaign: 'Summer Collection', allocated: 45000, spent: 42000, remaining: 3000 },
                    { campaign: 'Product Launch', allocated: 65000, spent: 58000, remaining: 7000 },
                    { campaign: 'Holiday Special', allocated: 35000, spent: 32000, remaining: 3000 },
                    { campaign: 'Brand Awareness', allocated: 25000, spent: 22000, remaining: 3000 },
                    { campaign: 'Loyalty Program', allocated: 30000, spent: 26000, remaining: 4000 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="campaign" 
                    tick={{ fill: theme.palette.text.secondary }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="allocated" name="Budget Allocated" fill={theme.palette.info.main} barSize={20} />
                  <Bar dataKey="spent" name="Budget Spent" fill={theme.palette.primary.main} barSize={20} />
                  <Bar dataKey="remaining" name="Budget Remaining" fill={theme.palette.success.main} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Campaign Cost Distribution"
              subtitle="Budget allocation by category"
              icon={<CampaignIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Ad Spend', value: 45 },
                      { name: 'Content Production', value: 25 },
                      { name: 'Design', value: 15 },
                      { name: 'Personnel', value: 10 },
                      { name: 'Other', value: 5 },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {chartColors.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Campaign Cost-Per-Result"
              subtitle="Efficiency metrics by campaign"
              icon={<CampaignIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={[
                    { campaign: 'Summer Collection', cpc: 1.2, cpm: 8.5, cpa: 15.5, efficiency: 92 },
                    { campaign: 'Product Launch', cpc: 1.5, cpm: 12.8, cpa: 18.2, efficiency: 85 },
                    { campaign: 'Holiday Special', cpc: 0.9, cpm: 7.2, cpa: 12.3, efficiency: 95 },
                    { campaign: 'Brand Awareness', cpc: 1.8, cpm: 15.5, cpa: 24.5, efficiency: 78 },
                    { campaign: 'Loyalty Program', cpc: 1.4, cpm: 10.5, cpa: 16.8, efficiency: 88 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="campaign" 
                    tick={{ fill: theme.palette.text.secondary }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value, name) => {
                      if (name === 'efficiency') return [`${value}%`, 'Efficiency'];
                      return [`$${value}`, name.toUpperCase()];
                    }}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="cpc" name="cpc" fill={theme.palette.primary.main} barSize={10} />
                  <Bar yAxisId="left" dataKey="cpm" name="cpm" fill={theme.palette.secondary.main} barSize={10} />
                  <Bar yAxisId="left" dataKey="cpa" name="cpa" fill={theme.palette.warning.main} barSize={10} />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="efficiency" 
                    name="efficiency" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={3} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FinancialAnalytics;